import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/modules/auth/hooks/useAuth";

type SearchItem = {
  id: string;
  code?: string;
  title: string;
  status?: string;
};

type LinkableType = "task" | "action" | "note" | "milestone";

const TYPE_META: Record<LinkableType, { label: string; plural: string; endpoint: string }> = {
  task: { label: "Task", plural: "tasks", endpoint: "/tasks" },
  action: { label: "Action", plural: "actions", endpoint: "/actions" },
  note: { label: "Note", plural: "notes", endpoint: "/notes" },
  milestone: { label: "Milestone", plural: "milestones", endpoint: "/milestones" },
};

type Props = {
  isOpen: boolean;
  onClose: () => void;
  type: LinkableType;
  meetingId: string;
  linkedIds?: string[];
  onLinked: () => void;
};

export function LinkItemModal({
  isOpen,
  onClose,
  type,
  meetingId,
  linkedIds = [],
  onLinked,
}: Props) {
  const { accessToken } = useAuth();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchItem[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setResults([]);
      setSelectedId(null);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const endpoint = TYPE_META[type].endpoint;
        const res = await apiFetch<{ data: SearchItem[] }>(endpoint, {
          token: accessToken,
          query: { search: query, limit: 20, page: 1 },
        });
        setResults(res.data ?? []);
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, isOpen, type, accessToken]);

  async function handleLink() {
    if (!selectedId) return;
    setIsLinking(true);
    try {
      const endpoint = `${TYPE_META[type].endpoint}/${selectedId}`;
      await apiFetch(endpoint, {
        method: "PUT",
        body: { meetingId },
        token: accessToken,
      });
      onLinked();
      onClose();
    } catch (e) {
      console.error("[LinkItemModal] link failed:", e);
    } finally {
      setIsLinking(false);
    }
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.4)",
          zIndex: 9998,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: "540px",
          maxHeight: "520px",
          background: "#fff",
          borderRadius: "0.75rem",
          border: "1px solid hsl(210,30%,85%)",
          boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
          display: "flex",
          flexDirection: "column",
          zIndex: 9999,
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid hsl(210,30%,85%)",
            flexShrink: 0,
          }}
        >
          <span style={{ fontSize: "15px", fontWeight: 600 }}>
            Link Existing {TYPE_META[type].label}
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: "28px",
              height: "28px",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: "4px",
              fontSize: "18px",
              color: "hsl(215,16%,47%)",
            }}
          >
            ×
          </button>
        </div>

        <div
          style={{
            padding: "12px 20px",
            borderBottom: "1px solid hsl(210,30%,85%)",
            flexShrink: 0,
          }}
        >
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`Search ${TYPE_META[type].plural}...`}
            style={{
              width: "100%",
              height: "36px",
              padding: "0 12px",
              border: "1px solid hsl(210,30%,85%)",
              borderRadius: "0.375rem",
              fontSize: "13px",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          {linkedIds.length > 0 && (
            <div
              style={{
                fontSize: "11px",
                color: "hsl(215,16%,47%)",
                padding: "4px 0 0 0",
              }}
            >
              {linkedIds.length} item{linkedIds.length === 1 ? "" : "s"} already linked are hidden
            </div>
          )}
        </div>

        {(() => {
          const filteredResults = results.filter((item) => !linkedIds.includes(item.id));
          return (
        <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
          {isSearching && (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: "hsl(215,16%,47%)",
                fontSize: "13px",
              }}
            >
              Searching...
            </div>
          )}
          {!isSearching && results.length === 0 && (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: "hsl(215,16%,47%)",
                fontSize: "13px",
              }}
            >
              {query
                ? "No results found"
                : `Type to search ${TYPE_META[type].plural}`}
            </div>
          )}
          {!isSearching && results.length > 0 && filteredResults.length === 0 && (
            <div
              style={{
                padding: "20px",
                textAlign: "center",
                color: "hsl(215,16%,47%)",
                fontSize: "13px",
              }}
            >
              All matching items are already linked to this meeting
            </div>
          )}
          {!isSearching &&
            filteredResults.map((item) => (
              <div
                key={item.id}
                onClick={() => setSelectedId(item.id === selectedId ? null : item.id)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 20px",
                  cursor: "pointer",
                  background: selectedId === item.id ? "hsl(210,55%,92%)" : "transparent",
                  borderBottom: "1px solid hsl(210,30%,90%)",
                }}
              >
                <div
                  style={{
                    width: "16px",
                    height: "16px",
                    borderRadius: "4px",
                    border:
                      selectedId === item.id
                        ? "2px solid hsl(210,68%,55%)"
                        : "2px solid hsl(210,30%,75%)",
                    background: selectedId === item.id ? "hsl(210,68%,55%)" : "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  {selectedId === item.id && (
                    <span style={{ color: "white", fontSize: "10px", fontWeight: 700 }}>✓</span>
                  )}
                </div>
                <span
                  style={{
                    fontFamily: "monospace",
                    fontSize: "11px",
                    color: "hsl(215,16%,47%)",
                    flexShrink: 0,
                    minWidth: "80px",
                  }}
                >
                  {item.code ?? ""}
                </span>
                <span
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.title}
                </span>
                {item.status && (
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      padding: "2px 8px",
                      borderRadius: "99px",
                      background: "hsl(210,55%,92%)",
                      color: "hsl(210,100%,28%)",
                      flexShrink: 0,
                    }}
                  >
                    {item.status.replace(/_/g, " ")}
                  </span>
                )}
              </div>
            ))}
        </div>
          );
        })()}

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "8px",
            padding: "12px 20px",
            borderTop: "1px solid hsl(210,30%,85%)",
            flexShrink: 0,
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              height: "32px",
              padding: "0 16px",
              border: "1px solid hsl(210,30%,85%)",
              borderRadius: "0.375rem",
              background: "white",
              cursor: "pointer",
              fontSize: "13px",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleLink}
            disabled={!selectedId || isLinking}
            style={{
              height: "32px",
              padding: "0 16px",
              border: "none",
              borderRadius: "0.375rem",
              background: selectedId ? "hsl(210,68%,55%)" : "hsl(210,30%,85%)",
              color: selectedId ? "white" : "hsl(215,16%,47%)",
              cursor: selectedId ? "pointer" : "not-allowed",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            {isLinking ? "Linking..." : "Link Selected"}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
